import {useApolloClient, useMutation} from '@apollo/client'
import axios from 'axios'
import {useCallback, useEffect, useRef, useState} from 'react'

import {CREATE_UPLOAD, CREATE_UPLOAD_PRESIGNER} from '../../../../../../graphql/mutations/userData'
import {UPLOAD} from '../../../../../../graphql/queries/userData'

export const MAX_STORY_PHOTOS = 3
// Same limit as vault uploads (shared_components/forms/v3/formTools.js).
export const MAX_PHOTO_BYTES = 110000000
export const STORY_PHOTO_ACCEPT = '.jpg,.jpeg,.png,.heic'

const STORY_PHOTO_KIND = 'story_photo'
const ENCRYPTION_POLL_INTERVAL = 1500
const ENCRYPTION_POLL_LIMIT = 40

// The content types the API accepts for each extension (it rejects a
// mismatch). HEIC may go without one: browsers other than Safari report none.
const FILETYPES_BY_EXTENSION = {
  jpg: ['image/jpeg', 'image/jpg'],
  jpeg: ['image/jpeg', 'image/jpg'],
  png: ['image/png'],
  heic: ['', 'image/heic', 'image/heif']
}

const photoCount = count => (count === 1 ? '1 photo' : `${count} photos`)

export const STORY_PHOTO_MESSAGES = {
  added: count => (count === 1 ? 'Photo added.' : `${count} photos added.`),
  atLimit: 'A story can hold up to 3 photos. Remove one to add another.',
  failed: 'We couldn’t add that photo. Please try again.',
  overflow: count => `A story can hold up to 3 photos, so we added the first ${count}.`,
  partlyUnsupported: count => `${photoCount(count)} added. Some files weren’t added because the format isn’t supported (JPG, PNG or HEIC only).`,
  removed: 'Photo removed.',
  tooLarge: 'Sorry, we can’t support a file size of more than 110 MB.',
  unsupported: 'Sorry, this file format is not supported. Please use a JPG, PNG or HEIC photo.',
  virus: 'A virus was detected with this file. Please check your email for details.'
}

const extensionOf = filename => {
  const match = (/\.([^.]+)$/).exec(filename || '')

  return match ? match[1].toLowerCase() : ''
}

/** Whether a picked file is one of the formats story photos take (case-insensitive extension check). */
export const isSupportedPhoto = file => Object.hasOwn(FILETYPES_BY_EXTENSION, extensionOf(file.name))

// The browser's type when it agrees with the extension, otherwise the
// extension's usual type (blank for HEIC).
const filetypeFor = file => {
  const allowed = FILETYPES_BY_EXTENSION[extensionOf(file.name)]
  const reported = (file.type || '').toLowerCase()

  return allowed.includes(reported) ? reported : allowed[0]
}

/**
 * The photos on one Life Story answer (TK-3108): up to three, in display
 * order, each `{'upload-id', filename}`.
 *
 * Each picked photo goes straight to S3 through a presigned URL, is
 * registered as a 'story_photo' upload (the API resizes it, strips its
 * metadata and converts HEIC to JPG before encrypting it), and is saved to the
 * story as soon as it's ready (AC 4.1) through `save(nextPhotos)`. Removing
 * and reordering save right away too. `toast` shows the outcome in the
 * ticket's wording.
 *
 * `uploading` lists the photos still on their way ({key, filename}), in
 * selection order, so the screen can show their prints loading and hold its
 * exits until they land.
 */
const useStoryPhotos = ({userConfig, initialPhotos = [], save, toast}) => {
  const apolloClient = useApolloClient()
  const [createUploadPresigner] = useMutation(CREATE_UPLOAD_PRESIGNER)
  const [createUpload] = useMutation(CREATE_UPLOAD)

  const [photos, setPhotos] = useState(initialPhotos)
  const [uploading, setUploading] = useState([])
  // Mirrors the state for code that runs after awaits: several uploads can
  // land back to back, and each must build on the list the last one saved.
  const photosRef = useRef(initialPhotos)
  const uploadingRef = useRef([])
  const nextKeyRef = useRef(0)
  // Where the next selection's photos land: after every photo picked before
  // them, even from an earlier selection still uploading.
  const landingRef = useRef(Promise.resolve())
  const mountedRef = useRef(true)

  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  const commit = useCallback(nextPhotos => {
    photosRef.current = nextPhotos
    setPhotos(nextPhotos)

    return save(nextPhotos)
  }, [save])

  const setUploadingList = nextUploading => {
    uploadingRef.current = nextUploading

    if (mountedRef.current) setUploading(nextUploading)
  }

  const pollUntilEncrypted = uploadId => new Promise((resolve, reject) => {
    let attempts = 0

    const poll = () => {
      attempts += 1

      apolloClient.query({query: UPLOAD, variables: {id: uploadId}, fetchPolicy: 'network-only'})
        .then(({data}) => {
          const status = data.upload.status

          if (status === 'encrypted') return resolve(data.upload)
          if (status === 'virus_detected') return reject(new Error('virus_detected'))
          if (status === 'failed') return reject(new Error('failed'))
          // A timed-out upload has not passed processing and must not be attached to the story.
          if (attempts >= ENCRYPTION_POLL_LIMIT) return reject(new Error('failed'))

          return setTimeout(poll, ENCRYPTION_POLL_INTERVAL)
        })
        .catch(reject)
    }

    poll()
  })

  const uploadPhoto = file => {
    const filetype = filetypeFor(file)

    return createUploadPresigner({variables: {filename: file.name}})
      .then(({data}) => {
        const presigner = data.createUploadPresigner

        // The same type createUpload records, so the stored object agrees with
        // it. A typeless HEIC goes up with no Content-Type at all.
        return axios.put(presigner.presignedUrl, file, {headers: filetype ? {'Content-Type': filetype} : {}})
          .then(() => createUpload({
            variables: {
              input: {
                filename: presigner.filename,
                filesize: file.size,
                everplanId: userConfig.get('everplan-id'),
                originalFileKey: presigner.fileObjectKey,
                filetype,
                kind: STORY_PHOTO_KIND
              }
            }
          }))
      })
      .then(({data}) => pollUntilEncrypted(data.createUpload.id).then(() => data.createUpload))
  }

  const room = () => MAX_STORY_PHOTOS - photosRef.current.length - uploadingRef.current.length

  /** Adds picked files: the supported ones that fit, in selection order (AC 1.4–1.9). */
  const addFiles = fileList => {
    const files = Array.from(fileList || [])

    if (files.length === 0) return Promise.resolve()

    if (room() <= 0) {
      toast(STORY_PHOTO_MESSAGES.atLimit)

      return Promise.resolve()
    }

    const supported = files.filter(isSupportedPhoto)
    const unsupportedCount = files.length - supported.length
    const fitting = supported.filter(file => file.size <= MAX_PHOTO_BYTES)
    const tooLargeCount = supported.length - fitting.length
    const accepted = fitting.slice(0, room())
    const overflowed = accepted.length < fitting.length

    if (accepted.length === 0) {
      toast(tooLargeCount > 0 ? STORY_PHOTO_MESSAGES.tooLarge : STORY_PHOTO_MESSAGES.unsupported)

      return Promise.resolve()
    }

    const entries = accepted.map(file => {
      nextKeyRef.current += 1

      return {key: `photo-${nextKeyRef.current}`, filename: file.name}
    })

    setUploadingList([...uploadingRef.current, ...entries])

    // Uploads run side by side but land in selection order, across
    // selections too, so the first photos picked are the first ones shown.
    const outcomes = accepted.map(file => uploadPhoto(file).then(upload => ({upload}), error => ({error})))
    let addedCount = 0
    let failure = null

    const landed = outcomes.reduce((previous, outcome, index) => previous.then(() => outcome).then(({upload, error}) => {
      const entry = entries[index]

      setUploadingList(uploadingRef.current.filter(pending => pending.key !== entry.key))

      if (error) {
        failure = error.message === 'virus_detected' ? STORY_PHOTO_MESSAGES.virus : (failure || STORY_PHOTO_MESSAGES.failed)

        return null
      }

      if (!mountedRef.current) return null

      addedCount += 1

      return commit([...photosRef.current, {'upload-id': upload.id, filename: entry.filename}])
    }), landingRef.current)

    landingRef.current = landed.catch(() => {})

    // One message per selection (the toast shows one at a time), the most
    // important outcome first.
    return landed.then(() => {
      if (!mountedRef.current) return

      if (failure) toast(failure)
      else if (tooLargeCount > 0) toast(STORY_PHOTO_MESSAGES.tooLarge)
      else if (unsupportedCount > 0) toast(STORY_PHOTO_MESSAGES.partlyUnsupported(addedCount))
      else if (overflowed) toast(STORY_PHOTO_MESSAGES.overflow(addedCount))
      else toast(STORY_PHOTO_MESSAGES.added(addedCount))
    })
  }

  /** Removes a photo right away, no confirmation (AC 3.1). */
  const removePhoto = uploadId => {
    const nextPhotos = photosRef.current.filter(photo => photo['upload-id'] !== uploadId)

    if (nextPhotos.length === photosRef.current.length) return Promise.resolve()

    toast(STORY_PHOTO_MESSAGES.removed)

    return commit(nextPhotos)
  }

  /** Swaps two photos (AC 3.3). */
  const swapPhotos = (fromIndex, toIndex) => {
    const current = photosRef.current

    if (fromIndex === toIndex || !current[fromIndex] || !current[toIndex]) return Promise.resolve()

    const nextPhotos = [...current]

    nextPhotos[fromIndex] = current[toIndex]
    nextPhotos[toIndex] = current[fromIndex]

    return commit(nextPhotos)
  }

  return {
    photos,
    photosRef,
    uploading,
    busy: uploading.length > 0,
    atLimit: photos.length + uploading.length >= MAX_STORY_PHOTOS,
    addFiles,
    removePhoto,
    swapPhotos
  }
}

export default useStoryPhotos
